import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@tempo-io/sui-class-utils';
import { DragIndicator } from '@tempo-io/sui-icons';
import { type ReactNode, useMemo } from 'react';

interface SortableCustomFieldProps {
  fieldId: UniqueIdentifier;
  isDraggable: boolean;
  children: ReactNode;
}

export const SortableCustomField = ({
  fieldId,
  isDraggable,
  children,
}: SortableCustomFieldProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldId,
    disabled: !isDraggable,
  });

  const style = useMemo(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      ...(isDragging ? { zIndex: 1, position: 'relative' as const } : {}),
    }),
    [transform, transition, isDragging],
  );

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <div className="flex-1 min-w-0">{children}</div>
      {isDraggable && (
        <div
          {...listeners}
          {...attributes}
          className={cn(
            'mt-1 shrink-0 text-icon-neutral-tertiary hover:text-icon-neutral-primary',
            {
              'cursor-grab': !isDragging,
              'cursor-grabbing': isDragging,
            },
          )}
        >
          <DragIndicator size={16} />
        </div>
      )}
    </div>
  );
};
